import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Security,
  Email,
  Phone,
  Person,
  FitnessCenter,
  ArrowForward,
  ArrowBack,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { ParticipantService } from '../../services/participantService';
import VerificationService, { type VerificationStatus } from '../../services/verificationService';
import EmailVerification from './EmailVerification';
import PhoneVerification from './PhoneVerification';
import LoadingSpinner from '../common/LoadingSpinner';
import type { Participant } from '../../types';

const MultiStepVerification: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Form data
  const [participantId, setParticipantId] = useState('');
  const [email, setEmail] = useState('');
  const [studyCode, setStudyCode] = useState('');
  const [studyName, setStudyName] = useState('');
  const [participantData, setParticipantData] = useState<Participant | null>(null);
  const [, setVerificationStatus] = useState<VerificationStatus | null>(null);
  
  // Step management
  const [activeStep, setActiveStep] = useState(0);
  const [stepsCompleted, setStepsCompleted] = useState<boolean[]>([false, false, false, false]);

  // Dynamic steps based on whether participant has phone
  const getSteps = () => {
    if (participantData?.phoneNumber) {
      return [
        'Enter Information',
        'Email Verification',
        'Phone Verification',
        'Complete Enrollment'
      ];
    } else {
      return [
        'Enter Information',
        'Email Verification',
        'Complete Enrollment'
      ];
    }
  };

  const steps = getSteps();

  // Get data from navigation state if available
  useEffect(() => {
    if (location.state) {
      const { participantId: navParticipantId, email: navEmail, studyCode: navStudyCode } = location.state as any;
      if (navParticipantId) setParticipantId(navParticipantId);
      if (navEmail) setEmail(navEmail);
      if (navStudyCode) setStudyCode(navStudyCode);
    }
  }, [location.state]);

  const handleInitialVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!participantId.trim() || !email.trim()) {
      setError('Please enter both Participant ID and email address');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const response = await ParticipantService.verifyParticipant(
        participantId.trim(),
        email.trim().toLowerCase()
      );

      if (response.success && response.data) {
        setParticipantData(response.data);
        setStudyCode(response.data.studyCode);
        setStudyName('Research Study'); // Default study name since Participant type doesn't have studyName
        
        // Check verification status
        const statusResponse = await VerificationService.getVerificationStatus(
          participantId.trim(),
          email.trim().toLowerCase(),
          response.data.studyCode
        );
        
        if (statusResponse.data) {
          setVerificationStatus(statusResponse.data);
          
          const hasPhone = response.data.phoneNumber;
          const maxSteps = hasPhone ? 4 : 3;
          const newStepsCompleted = new Array(maxSteps).fill(false);
          newStepsCompleted[0] = true; // Enter Information is always completed
          
          // If already verified, skip to appropriate step
          if (statusResponse.data.verificationStatus === 'fully_verified') {
            newStepsCompleted[1] = true; // Email verified
            if (hasPhone) {
              newStepsCompleted[2] = true; // Phone verified
              setActiveStep(3); // Go to Complete Enrollment (step 3)
            } else {
              setActiveStep(2); // Go to Complete Enrollment (step 2 when no phone)
            }
          } else if (statusResponse.data.verificationStatus === 'email_verified') {
            newStepsCompleted[1] = true; // Email verified
            if (hasPhone) {
              setActiveStep(2); // Go to Phone Verification
            } else {
              setActiveStep(2); // Go to Complete Enrollment (no phone step)
            }
          } else if (statusResponse.data.verificationStatus === 'phone_verified') {
            if (hasPhone) {
              newStepsCompleted[2] = true; // Phone verified
              setActiveStep(3); // Go to Complete Enrollment
            }
          } else {
            setActiveStep(1); // Go to Email Verification
          }
          
          setStepsCompleted(newStepsCompleted);
        } else {
          const hasPhone = response.data.phoneNumber;
          const maxSteps = hasPhone ? 4 : 3;
          const newStepsCompleted = new Array(maxSteps).fill(false);
          newStepsCompleted[0] = true;
          setStepsCompleted(newStepsCompleted);
          setActiveStep(1);
        }
        
        setSuccess('Information verified! Proceeding to email verification.');
      } else {
        setError(response.error || 'Verification failed. Please check your information and try again.');
      }
    } catch (err: any) {
      setError('Verification failed: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleEmailVerificationComplete = (status: VerificationStatus) => {
    setVerificationStatus(status);
    const hasPhone = participantData?.phoneNumber;
    const maxSteps = hasPhone ? 4 : 3;
    const newStepsCompleted = new Array(maxSteps).fill(false);
    newStepsCompleted[0] = true; // Enter Information
    newStepsCompleted[1] = true; // Email Verification
    setStepsCompleted(newStepsCompleted);
    
    // Move to phone verification if available, otherwise to complete enrollment
    setActiveStep(hasPhone ? 2 : 2);
    setSuccess('Email verification successful!');
  };

  const handlePhoneVerificationComplete = (status: VerificationStatus) => {
    setVerificationStatus(status);
    const maxSteps = 4; // Only called when phone exists
    const newStepsCompleted = new Array(maxSteps).fill(false);
    newStepsCompleted[0] = true; // Enter Information
    newStepsCompleted[1] = true; // Email Verification
    newStepsCompleted[2] = true; // Phone Verification
    setStepsCompleted(newStepsCompleted);
    setActiveStep(3); // Go to Complete Enrollment
    setSuccess('Phone verification successful!');
  };

  const handleCompleteEnrollment = () => {
    if (participantData) {
      const hasPhone = participantData?.phoneNumber;
      const maxSteps = hasPhone ? 4 : 3;
      const newStepsCompleted = new Array(maxSteps).fill(true);
      setStepsCompleted(newStepsCompleted);
      navigate('/participant/flow', {
        state: {
          participantId,
          email,
          studyCode,
          studyName,
          participantData
        }
      });
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  // const handleNext = () => {
  //   if (activeStep < steps.length - 1) {
  //     setActiveStep(activeStep + 1);
  //   }
  // };

  if (loading && activeStep === 0) {
    return <LoadingSpinner message="Verifying your information..." />;
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Security sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
            Participant Verification
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Verify your enrollment and complete the verification process
          </Typography>
        </Box>

        {/* Progress Stepper */}
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label, index) => {
              const getStepIcon = () => {
                switch (index) {
                  case 0: return <Person />;
                  case 1: return <Email />;
                  case 2: return <Phone />;
                  case 3: return <FitnessCenter />;
                  default: return <Person />;
                }
              };
              
              return (
                <Step key={label} completed={stepsCompleted[index]}>
                  <StepLabel icon={getStepIcon()}>{label}</StepLabel>
                </Step>
              );
            })}
          </Stepper>
        </Paper>

        {/* Error/Success Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Step Content */}
        <Paper elevation={3} sx={{ p: 4 }}>
          {/* Step 0: Initial Information */}
          {activeStep === 0 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 3 }}>
                Enter Your Information
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Please enter your Participant ID and email address to begin the verification process.
              </Typography>

              <Box component="form" onSubmit={handleInitialVerification}>
                <TextField
                  fullWidth
                  label="Participant ID"
                  value={participantId}
                  onChange={(e) => setParticipantId(e.target.value)}
                  placeholder="P1234567890ABCDEF"
                  sx={{ mb: 3 }}
                  InputProps={{
                    startAdornment: <Person sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
                
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  sx={{ mb: 3 }}
                  InputProps={{
                    startAdornment: <Email sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
                
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={loading}
                  endIcon={<ArrowForward />}
                >
                  {loading ? 'Verifying...' : 'Verify Information'}
                </Button>
              </Box>
            </Box>
          )}

          {/* Step 1: Email Verification */}
          {activeStep === 1 && participantData && (
            <Box>
              <EmailVerification
                participantId={participantId}
                email={email}
                studyCode={studyCode}
                studyName={studyName}
                onVerificationComplete={handleEmailVerificationComplete}
              />
            </Box>
          )}

          {/* Step 2: Phone Verification (only if participant has phone) */}
          {participantData?.phoneNumber && activeStep === 2 && participantData && (
            <Box>
              <PhoneVerification
                participantId={participantId}
                phoneNumber={participantData.phoneNumber || ''}
                studyCode={studyCode}
                studyName={studyName}
                onVerificationComplete={handlePhoneVerificationComplete}
              />
            </Box>
          )}

          {/* Step 3 (or 2 if no phone): Complete Enrollment */}
          {((participantData?.phoneNumber && activeStep === 3) || (!participantData?.phoneNumber && activeStep === 2)) && participantData && (
            <Box>
              <Typography variant="h6" sx={{ mb: 3 }}>
                Verification Complete!
              </Typography>
              
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Study Information
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <Person />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Participant ID" 
                        secondary={participantId}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Email />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Email" 
                        secondary={email}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <FitnessCenter />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Study" 
                        secondary={studyName}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>

              <Typography variant="body1" sx={{ mb: 3 }}>
                Your email has been verified successfully! You can now proceed to connect your Fitbit account and complete your enrollment.
              </Typography>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  startIcon={<ArrowBack />}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  onClick={handleCompleteEnrollment}
                  endIcon={<FitnessCenter />}
                  sx={{ flex: 1 }}
                >
                  Continue to Fitbit Authorization
                </Button>
              </Box>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default MultiStepVerification;
